import { forwardRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../common/Media';
import FadeImage from './FadeImage';
import { onCardKeyDown } from './CardGrid';
import { ReadablePalette } from '../common/Color';
import { u } from '../common/Units';
import { MEDIA_PREFIX } from '../Settings';
import { DMItem, getDMImageUrl, getDMTypeKey, isDMContextType, isDMVirtualExperienceOrPart } from '../services/DigitaltMuseum';
import { NoImageIcon } from './DMIcons';
import VMIcon from '../assets/icon_vm.svg?react';
import FolderIcon from '../assets/icon_folder.svg?react';

interface DMResultCardProps {
  item: DMItem,
  ownerName?: string,
  palette: ReadablePalette,
  onClick: () => void,
  /** Wide card for a single object: a large image beside the text (above it in portrait) */
  wide?: boolean
}

/** URL of a card's image (larger for the wide card) */
export const getDMCardImageUrl = (mediaIdentifier: string, wide?: boolean): string =>
  `${MEDIA_PREFIX}${getImageUrl(getDMImageUrl(mediaIdentifier), { dimension: wide ? "1200x1200" : "400x400" })}`;

const Card = styled.li<{ $palette: ReadablePalette }>`
  display: flex;
  flex-direction: column;
  border-radius: ${u(12)};
  overflow: hidden;
  background-color: ${p => p.$palette.cardColor};
  color: ${p => p.$palette.textColor};
  cursor: pointer;
  transition: transform 0.15s;

  &:active {
    transform: scale(0.98);
  }

  &.wide {
    flex-direction: row;

    &:active {
      transform: scale(0.99);
    }
  }

  @media (orientation: portrait) {
    &.wide {
      flex-direction: column;
    }
  }
`;

const CardImage = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  /* 4:3. Padding is used instead of aspect-ratio for older iOS */
  padding-top: 72%;
  background-color: ${p => p.$palette.imageBackgroundColor};

  & img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .wide > & {
    flex: 0 0 60%;
    padding-top: 0;
    height: ${u(440)};
  }

  @media (orientation: portrait) {
    .wide > & {
      flex: none;
      height: ${u(400)};
    }
  }

  & .noImage {
    position: absolute;
    top: 35%;
    left: 40%;
    width: 20%;
    height: 30%;
    opacity: 0.4;
  }
`;

/** Icon in the top left corner: VM icon on virtual experiences (like on digitaltmuseum.org), folder icon on exhibitions and folders */
const Badge = styled.span`
  position: absolute;
  top: ${u(12)};
  left: ${u(12)};
  width: ${u(28)};
  height: ${u(31)};
  color: #fff;
  filter: drop-shadow(0 0 ${u(4)} rgba(0, 0, 0, 0.6));

  & svg {
    width: 100%;
    height: 100%;
  }
`;

const CardText = styled.div<{ $palette: ReadablePalette }>`
  padding: ${u(16)} ${u(18)} ${u(18)};
  font-size: 0.85em;
  line-height: 1.4;

  /* Larger text, centered beside the image */
  .wide > & {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: ${u(40)};
    font-size: 1em;

    & h2 {
      margin-bottom: ${u(12)};
      font-size: 1.6em;
      line-height: 1.2;
      -webkit-line-clamp: 4;
    }

    & p {
      white-space: normal;
    }
  }

  & h2 {
    margin: 0 0 ${u(6)};
    font-size: 1.1em;
    font-weight: normal;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }

  & p {
    margin: 0;
    color: ${p => p.$palette.mutedTextColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/**
 * A DM object in the result list: image, type badge, title, type and year, and owner.
 * Forwards the ref, so the card can open a lightbox (LightboxItem)
 * @returns {JSX.Element} Component template
 */
const DMResultCard = forwardRef<HTMLLIElement, DMResultCardProps>(({ item, ownerName, palette, onClick, wide }, ref) => {
  const { t } = useTranslation();
  const imageUrl = item.mediaIdentifier ? getDMCardImageUrl(item.mediaIdentifier, wide) : undefined;
  const typeLabel = (type?: string) => t(`dm.type.${type}`, { defaultValue: type || "" });

  return (
    <Card ref={ref} className={wide ? "wide" : undefined} $palette={palette} onClick={onClick} onKeyDown={onCardKeyDown} role="button" tabIndex={0} aria-label={item.title}>
      <CardImage $palette={palette}>
        {item.mediaIdentifier
          ? <FadeImage src={imageUrl!} alt="" loading="lazy"/>
          : <NoImageIcon className="noImage"/>}
        {isDMVirtualExperienceOrPart(item.subtype) && <Badge title={typeLabel("VirtualExperience")}><VMIcon/></Badge>}
        {isDMContextType(item.type) && <Badge title={typeLabel(item.type)}><FolderIcon/></Badge>}
      </CardImage>
      <CardText $palette={palette}>
        <h2>{item.title}</h2>
        <p>{[typeLabel(getDMTypeKey(item.type, item.subtype)), item.year].filter(Boolean).join(", ")}</p>
        <p>{ownerName || " "}</p>
      </CardText>
    </Card>
  );
});

export default DMResultCard;
